import { type MouseEvent, type ReactNode } from 'react'
import { Collapsible } from '@highlight/ui'

import { SidepanelSection } from '../../../components/sidepanel/nested/section'
import { SidepanelSectionHeader } from '../../../components/sidepanel/nested/section-header'
import { SidepanelSectionIcon } from '../../../components/sidepanel/nested/section-icon'
import { SidepanelSectionLabel } from '../../../components/sidepanel/nested/section-label'
import { SidepanelSectionTitleGroup } from '../../../components/sidepanel/nested/section-title-group'

interface MeetingViewSectionProps {
  icon: ReactNode
  title: string
  /** Unread count, beside the title rather than in the trailing slot. */
  badge?: ReactNode
  /** Right-aligned slot — the "Preparing" status, a copy button. */
  trailing?: ReactNode
  /** Open shows the whole thing; closed shows the header alone. */
  expanded: boolean
  /** Omit for a static header with no chevron. */
  onToggleExpanded?: () => void
  /** `data-tour-section` value so the meeting tour can spotlight this section. */
  tourId?: string
  className?: string
  children?: ReactNode
}

const stopPropagation = (event: MouseEvent) => event.stopPropagation()

/**
 * One Recap/meeting section, wearing the same header chrome as Home's
 * Upcoming / For You / Reminders / Recents rows.
 *
 * Disclosure is an instant show/hide. These sections wrap a whole meeting
 * summary, and `Collapsible.Content`'s height animation forces layout every
 * frame there and visibly janks. The `Collapsible` root exists only so the
 * title is the same trigger Home's sections use; the content mounts directly.
 * Only the chevron animates, on `transform`.
 */
export function MeetingViewSection({
  icon,
  title,
  badge,
  trailing,
  expanded,
  onToggleExpanded,
  tourId,
  className,
  children,
}: MeetingViewSectionProps) {
  const heading = (
    <>
      <SidepanelSectionIcon>{icon}</SidepanelSectionIcon>
      <SidepanelSectionLabel className="min-w-0">
        <span className="truncate">{title}</span>
        {badge}
      </SidepanelSectionLabel>
    </>
  )

  return (
    <SidepanelSection data-tour-section={tourId} className={className}>
      <Collapsible open={expanded} onOpenChange={onToggleExpanded} className="w-full gap-1.5">
        <SidepanelSectionHeader
          className={onToggleExpanded ? 'cursor-pointer' : 'hover:bg-transparent'}
          onClick={onToggleExpanded}
        >
          {onToggleExpanded ? (
            // The whole header toggles too; the trigger's own click must not
            // bubble into it and toggle twice.
            <SidepanelSectionTitleGroup onClick={stopPropagation}>{heading}</SidepanelSectionTitleGroup>
          ) : (
            <SidepanelSectionTitleGroup static>{heading}</SidepanelSectionTitleGroup>
          )}
          {trailing && (
            <div className="relative z-10 ml-auto flex shrink-0 items-center" onClick={stopPropagation}>
              {trailing}
            </div>
          )}
        </SidepanelSectionHeader>
        {expanded ? children : null}
      </Collapsible>
    </SidepanelSection>
  )
}
